import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  Renderer2,
  Signal,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MapItem } from '@capturum/ui/api';
import { CapturumButtonModule } from '@capturum/ui/button';
import { DialogLayoutComponent } from '@core/components/dialog-layout/dialog-layout.component';
import { PanelLayoutComponent } from '@core/components/panel-layout/panel-layout.component';
import { KEY_MODIFIER } from '@core/configs/token.config';
import { keyMatches } from '@core/helpers/key-event.helper';
import { KeyModifier, ModificationKey } from '@core/interfaces/key-modifier.interface';
import { TourService } from '@core/services/tour.service';
import { Application, Parameter } from '@features/applications/interfaces/action.interface';
import { NodeEditorComponentDirective } from '@features/flow-ui-creator/directives/node-editor-component.directive';
import { FlowNode } from '@features/flow-ui-creator/interfaces/flow-ui.interface';
import {
  JSONValue,
  NodeDataParameters,
  NodeEditorCondition,
  NodeEditorCronExpression,
  NodeEditorEneqtoData,
  NodeEditorEneqtoDataParameter,
  NodeKeyMapping,
  NodeType,
} from '@features/flow-ui-creator/interfaces/node.interface';
import { NodeIconPipe } from '@features/flow-ui-creator/pipes/node-icon.pipe';
import { FlowApiService } from '@features/flow-ui-creator/services/flow-api.service';
import { FlowService } from '@features/flow-ui-creator/services/flow.service';
import { CoreNodesEnum, TriggerNodesEnum } from '@features/flow-ui-creator/services/graph.utils';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingComponent } from '@shared/components/loading/loading.component';
import { flattenNestedArray } from '@shared/helpers/array.helper';
import { SlugPipe } from '@shared/pipes/slug.pipe';
import { ActionService } from '@shared/services/action.service';
import { CommonService } from '@shared/services/common.service';
import { FormulaVariablesService } from '@shared/services/formula-variables.service';
import { AccordionModule } from 'primeng/accordion';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SplitterModule } from 'primeng/splitter';
import { map } from 'rxjs';
import { DataSchemaComponent } from '../data-schema/data-schema.component';
import { DataSchemaService } from '../data-schema/services/data-schema.service';
import { VariablePickerSearchComponent } from '../variable-picker/variable-picker-search/variable-picker-search.component';
import { VariablePickerTreeComponent } from '../variable-picker/variable-picker-tree/variable-picker-tree.component';
import { buildFields, buildFieldsFromPathMap, countMatches } from '../variable-picker/variable-picker.utils';

const defaultPanelSize: Tuple<number, 3> = [30, 69, 1];

@Component({
  selector: 'app-node-editor',
  imports: [
    DialogLayoutComponent,
    SplitterModule,
    NodeEditorComponentDirective,
    TranslateModule,
    PanelLayoutComponent,
    AccordionModule,
    NodeIconPipe,
    DataSchemaComponent,
    VariablePickerTreeComponent,
    VariablePickerSearchComponent,
    CapturumButtonModule,
    LoadingComponent,
  ],
  templateUrl: './node-editor.component.html',
  styleUrl: './node-editor.component.scss',
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [DataSchemaService, SlugPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NodeEditorComponent {
  nodeEditorComponent = viewChild(NodeEditorComponentDirective);
  dialogRef = inject(DynamicDialogRef);
  node: FlowNode;
  previousSteps = signal<FlowNode[]>([]);
  panelSize = signal<Tuple<number, 3>>(defaultPanelSize);
  executionResult = signal<JSONValue>(null);
  nodeType = NodeType;
  triggerNodeTypes = [NodeType.Trigger, NodeType.End, NodeType.Terminate];
  /** Cross-step variable filter query (the "Previous steps" search box). */
  variableFilter = signal('');

  /** Built variable fields per previous step, rebuilt only when step data changes. */
  private stepFields = computed(() => {
    return this.previousSteps().map((step) => {
      const fields =
        step.nodeData.type === NodeType.HttpAction
          ? buildFieldsFromPathMap((step.nodeData.stubs?.() as Record<string, unknown>) ?? {})
          : buildFields(step.nodeData.execution?.result() ?? null);

      return { step, fields };
    });
  });

  /** Per-step match counts against the current filter. */
  private stepMatches = computed(() => {
    const query = this.variableFilter();

    return this.stepFields().map(({ step, fields }) => ({ step, count: countMatches(fields, query) }));
  });

  /** Steps to render: all when not filtering, otherwise only those with matches. */
  displaySteps = computed(() => {
    const query = this.variableFilter().trim();
    const matches = this.stepMatches();

    return query ? matches.filter((entry) => entry.count > 0) : matches;
  });

  totalMatches = computed(() => this.stepMatches().reduce((sum, entry) => sum + entry.count, 0));

  /** Panel values (step ids) the accordion opens: every rendered (matching) step. */
  openStepIds = computed(() => this.displaySteps().map((entry) => entry.step.id));

  mapping: NodeKeyMapping;
  buttonDisabled: Signal<boolean>;

  readonly #dialogConfig = inject(DynamicDialogConfig);
  readonly flowService = input<FlowService>();
  readonly #apiService = inject(FlowApiService);
  readonly #dataSchemaService = inject(DataSchemaService);
  readonly #formulaVariables = inject(FormulaVariablesService);
  readonly #actionService = inject(ActionService);
  readonly #injector = inject(Injector);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #commonService = inject(CommonService);
  readonly #slugPipe = inject(SlugPipe);
  readonly #tourService = inject(TourService);
  readonly #elementRef = inject<ElementRef<HTMLElement>>(ElementRef<HTMLElement>);
  readonly #renderer = inject(Renderer2);
  readonly #document = inject<Document>(DOCUMENT);
  readonly #keyModifier = inject<KeyModifier>(KEY_MODIFIER);
  readonly #destroyRef = inject(DestroyRef);

  #removeListeners = new Map<string, () => void>();

  constructor() {
    this.node = this.#dialogConfig.data?.node;

    effect(() => {
      this.mapping = this.#dataSchemaService.getDataSchemaKeyMapping()();
      this.#formulaVariables.setVariables(Object.keys(this.mapping ?? {}));
    });

    this.#actionService.clearResponseStubMapItem();
    this.buttonDisabled = computed(() => {
      return this.#commonService.getAddButtonDisabled();
    });

    afterNextRender({
      read: () => {
        const hostElement = this.#elementRef.nativeElement;

        if (hostElement) {
          this.#removeListeners.set(
            'keydown',
            this.#renderer.listen(this.#document, 'keydown', this.#keydownHandler.bind(this), {
              capture: false,
              passive: false,
            }),
          );
        }

        // First time a core node is opened, explain {{ }} templating.
        if ((Object.values(CoreNodesEnum) as string[]).includes(this.node.nodeData.type)) {
          this.#tourService.startForPage('core-node');
        }
      },
    });

    this.#destroyRef.onDestroy(async () => {
      this.#removeListeners.forEach((removeListener) => {
        removeListener();
      });
    });
  }

  save(): void {
    if (this.nodeEditorComponent().componentInstance.form.valid) {
      const data = this.nodeEditorComponent().componentInstance.formValue;

      this.node.nodeData.name = !this.triggerNodeTypes.includes(this.node.nodeData.type)
        ? data.label
        : this.node.nodeData.name;

      this.node.nodeData.parameters = data.parameters;

      this.close();
    } else {
      this.flowService().triggerFormlyTypeValidity();
      this.nodeEditorComponent().componentInstance.form.markAllAsTouched();
    }
  }

  componentIsRendered(): void {
    const connectedNodes = this.flowService()
      .getConnectedNodes(this.node.id)
      .map((node) => {
        if (node.nodeData.type === NodeType.HttpAction) {
          node.nodeData.execution.executed = true;
          node.nodeData.stubs = toSignal(
            this.#actionService.getApplicationActionStub(node.nodeData.data.application_id, node.nodeData.data.id).pipe(
              map((action) => {
                const maps = action.parameters?.reduce((acc, parameter) => {
                  const actionNameSlugify = this.#slugPipe.transform(node.nodeData.name || '', {
                    charmap: {
                      '-': '_', // replace '-' with '_'
                    },
                  });
                  const value = actionNameSlugify ? `${actionNameSlugify}.${parameter.key}` : parameter.key;

                  return {
                    ...acc,
                    [`${value}`]: parameter.data_type,
                  };
                }, {});

                const stubsOptions: MapItem[] = Object.keys(maps || {}).map((key) => {
                  return {
                    label: key,
                    value: key,
                  };
                });

                this.#actionService.setResponseStubMapItem(stubsOptions);

                return maps;
              }),
            ),
            {
              injector: this.#injector,
            },
          );
        }

        return node;
      });

    this.previousSteps.set(connectedNodes.reverse());
    this.#cdr.detectChanges();
  }

  close(): void {
    this.dialogRef.close(this.node);
    this.#commonService.setAddButtonDisabled(false);

    switch (this.node.nodeData.type) {
      case NodeType.Switch: {
        const outputLength = Object.entries(this.node.outputs).length;

        if (!(this.node.nodeData.parameters as NodeEditorCondition[])?.length && outputLength > 0) {
          for (const [key, _] of Object.entries(this.node.outputs)) {
            this.node.removeOutput(key);
          }
        }

        break;
      }

      case NodeType.HttpRequest:
      case NodeType.HttpAction:
      case NodeType.HttpResponse: {
        const parametersValue =
          this.node.nodeData.type === NodeType.HttpAction || this.node.nodeData.type === NodeType.HttpRequest
            ? this.node.nodeData.parameters
            : this.node.nodeData.parameters?.[0];

        const parameters = flattenNestedArray(Object.values(parametersValue || {})).map((parameter: Parameter) => {
          // Drop is_edit so closing mid-edit doesn't persist a stuck edit state (NEQ-858).
          const { children, id, value, is_edit, ...rest } = parameter;
          let parameterValue = value;

          if (parameter.category === 'error-emails' && Array.isArray(value)) {
            parameterValue = (value as unknown as string[])?.join(';');
          }

          return {
            ...rest,
            id: id ? String(id) : undefined, // BE requires id to be string
            value: parameterValue,
          };
        });

        this.node.nodeData = {
          ...this.node.nodeData,
          ...(this.node.nodeData.parameters && {
            parameters,
          }),
        };

        break;
      }

      case NodeType.EneqtoData: {
        const params = this.node.nodeData.parameters as NodeEditorEneqtoData['parameters'];
        const parameters: NodeEditorEneqtoDataParameter[] = [];

        if (params?.query) {
          parameters.push({ category: 'query', value: params.query });
        }

        for (const variable of params?.variables ?? []) {
          parameters.push({ category: 'variable', key: variable.key, value: variable.value });
        }

        this.node.nodeData = { ...this.node.nodeData, parameters: parameters as NodeDataParameters };

        break;
      }

      case NodeType.Trigger: {
        const nodeDataName = this.node.nodeData.name.toLowerCase();

        if (nodeDataName === TriggerNodesEnum.Schedule.toLowerCase()) {
          this.node.nodeData = {
            ...this.node.nodeData,
            ...(this.node.nodeData.parameters && {
              parameters: [this.node.nodeData.parameters as NodeEditorCronExpression],
            }),
          };
        }

        if (nodeDataName === TriggerNodesEnum.Api.toLowerCase()) {
          const data = this.nodeEditorComponent().componentInstance.formValue;
          const kind = data?.kind;

          this.node.nodeData.kind = kind;
        }

        if ([TriggerNodesEnum.Webhook.toLowerCase(), TriggerNodesEnum.Api.toLowerCase()].includes(nodeDataName)) {
          const parameters = this.nodeEditorComponent().componentInstance.formValue.parameters;

          this.node.nodeData.parameters = [
            ...(parameters.headers || []),
            ...(parameters.query || []),
            ...(parameters.body || []),
          ];
        }

        break;
      }

      default:
        break;
    }

    this.#resetNodeExecution();

    const pristine = this.#isPristine();

    // A real content edit (form value differs from its initial value) must mark
    // the flow dirty — node-data edits don't go through the rete area pipe that
    // tracks structural changes (connections/moves).
    if (!pristine) {
      this.flowService().setHasUnsavedChanges();
    }

    this.flowService().updateNode(this.node, pristine);
  }

  testStep(): void {
    switch (this.node.nodeData.type) {
      // Todo: expand this switch case to handle other node types
      case NodeType.HttpAction: {
        // Todo: This is a mock
        // Replace this with actual API call
        // This is just to show the flow node step execution, and show result panel
        const data = this.node.nodeData.data;
        const url = `${(data.application as Application).base_url}${data.endpoint}`;

        this.#apiService.executeStep(url).subscribe((response) => {
          this.node.nodeData.execution.executed = true;
          this.node.nodeData.execution.result.set(response);
          this.flowService().updateNode(this.node);
        });

        break;
      }
      case NodeType.Condition: {
        this.#testPreviousSteps();

        break;
      }

      default:
        break;
    }
  }

  executePreviousStep(step: FlowNode, index: number, event: MouseEvent): void {
    event.stopPropagation();

    const data = step.nodeData.data;
    const url = `${(data.application as Application).base_url}${data.endpoint}`;

    this.#apiService.executeStep(url).subscribe((response) => {
      step.nodeData.execution.executed = true;
      step.nodeData.execution.result.set(response);
      this.flowService().updateNode(step);
    });
  }

  #testPreviousSteps(): void {
    // @Todo: implement test for previous steps when BE is ready
  }

  #isPristine(): boolean {
    const data = this.nodeEditorComponent()?.componentInstance?.formValue;
    const initialData = this.nodeEditorComponent()?.componentInstance?.formInitialValue();

    return JSON.stringify(data) === JSON.stringify(initialData);
  }

  #resetNodeExecution(): void {
    this.node.nodeData.execution.executed = false;
    this.node.nodeData.execution.result.set(null);

    this.previousSteps()?.forEach((step) => {
      step.nodeData.execution.executed = false;
      step.nodeData.execution.result.set(null);
    });
  }

  #getNodeDataParameters(parameters: NodeDataParameters): NodeDataParameters {
    if (
      this.node.nodeData.type === NodeType.Trigger &&
      this.node.nodeData.name.toLowerCase() === TriggerNodesEnum.Schedule.toLowerCase()
    ) {
      return [parameters as NodeEditorCronExpression];
    }

    return parameters;
  }

  #keydownHandler(event: KeyboardEvent): void {
    const keyModifier = this.#keyModifier.modifiers as Record<ModificationKey, ModificationKey>;
    const saveKeyMatches: boolean = keyMatches(event, 'code', keyModifier.primary, 'KeyS');

    switch (true) {
      case saveKeyMatches:
        event.preventDefault();
        this.save();
        break;
      case event.code === 'Escape':
        event.preventDefault();
        this.close();
        break;
      default:
        break;
    }
  }
}
